import { withTrailingSlash, withoutTrailingSlash, resolveURL } from 'ufo'
import { useRequestURL, useError, useSiteConfig } from '#imports'
/**
 * Middleware to handle canonical URL redirects.
 *
 * This middleware ensures that:
 * 1. Static assets (CSS, JS, HTML) are not redirected
 * 2. URLs are redirected to their canonical form based on site configuration
 * 3. Trailing slash is added or removed according to site settings
 *
 * @returns {void|NavigationResponse} Redirects to canonical URL if needed
 *
 * @remarks
 * - Skips redirection during prerendering or on the client side
 * - Uses site configuration to determine canonical URL
 * - Preserves query parameters and hash in the canonical URL
 *
 * @example
 *  If site config requires trailing slash and current URL is '/page'
 *  Redirects to '/page/' with a 301 status code
 */
export default defineNuxtRouteMiddleware(() => {
  if (import.meta.prerender || import.meta.client) return

  const siteConfig = useSiteConfig()
  const error = useError()
  const url = useRequestURL()

  const originalUrl = url.href

  if (
    url.pathname.endsWith('.css') ||
    url.pathname.endsWith('.js') ||
    url.pathname.endsWith('.html')
  ) {
    return
  }
  let canonicalUrl =
    siteConfig.url && !import.meta.dev
      ? resolveURL(siteConfig.url, url.pathname, url.search, url.hash)
      : originalUrl

  if (siteConfig.trailingSlash) {
    canonicalUrl = siteConfig.trailingSlash
      ? withTrailingSlash(canonicalUrl, true)
      : withoutTrailingSlash(canonicalUrl, true)
  }

  if (canonicalUrl !== originalUrl && !error.value) {
    return navigateTo(canonicalUrl, { redirectCode: 301, external: true })
  }
})
